import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Users, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const VITALIS_LOGO = "https://vitalis-sports.ch/wp-content/uploads/2024/10/Asset-3.png";

export default function Kontakt() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    bereich: '',
    vorname: '',
    name: '',
    email: '',
    telefon: '',
    nachricht: ''
  });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    
    // Simulate form submission
    await new Promise(r => setTimeout(r, 1000));
    toast.success('Nachricht erfolgreich gesendet!');
    setFormData({ bereich: '', vorname: '', name: '', email: '', telefon: '', nachricht: '' });
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Extranet Banner */}
      <div className="bg-[#1e3a5f] text-white py-1.5 px-4 text-xs">
        <div className="max-w-7xl mx-auto flex items-center justify-end gap-3">
          <span className="text-white/70">Mitarbeiter-Bereich</span>
          <Link to="/login" className="flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 px-3 py-1 rounded text-xs font-medium transition-colors">
            <Users className="h-3 w-3" />
            Extranet
          </Link>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white py-4 px-6 flex items-center justify-between relative z-50 shadow-sm">
        <Link to="/" className="flex items-center">
          <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-10" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1">
          <Link to="/fitness" className="flex items-center gap-1 px-4 py-2 text-gray-700 hover:text-orange-500 font-medium">Fitness <ChevronDown className="h-4 w-4" /></Link>
          <Link to="/racket" className="flex items-center gap-1 px-4 py-2 text-gray-700 hover:text-orange-500 font-medium">Racket <ChevronDown className="h-4 w-4" /></Link>
          <Link to="/wellness" className="px-4 py-2 text-gray-700 hover:text-orange-500 font-medium">Wellness</Link>
          <Link to="/ueber-uns" className="flex items-center gap-1 px-4 py-2 text-gray-700 hover:text-orange-500 font-medium">Über uns <ChevronDown className="h-4 w-4" /></Link>
          <Link to="/kontakt" className="px-4 py-2 text-orange-500 font-medium">Kontakt</Link>
        </nav>
        <div className="flex items-center gap-4">
          <a href="https://www.courtsonline.ch/?a=Ng==" target="_blank" rel="noopener noreferrer" className="hidden sm:block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded font-bold text-sm">PLATZ BUCHEN</a>
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2 hover:bg-gray-100 rounded">{menuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}</button>
        </div>
      </header>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 bg-white z-40 pt-20 lg:hidden overflow-y-auto">
          <nav className="flex flex-col py-4">
            <Link to="/fitness" onClick={() => setMenuOpen(false)} className="px-6 py-4 text-xl font-bold text-gray-800 hover:text-orange-500 border-b border-gray-100">Fitness</Link>
            <Link to="/racket" onClick={() => setMenuOpen(false)} className="px-6 py-4 text-xl font-bold text-gray-800 hover:text-orange-500 border-b border-gray-100">Racket</Link>
            <Link to="/wellness" onClick={() => setMenuOpen(false)} className="px-6 py-4 text-xl font-bold text-gray-800 hover:text-orange-500 border-b border-gray-100">Wellness</Link>
            <Link to="/ueber-uns" onClick={() => setMenuOpen(false)} className="px-6 py-4 text-xl font-bold text-gray-800 hover:text-orange-500 border-b border-gray-100">Über uns</Link>
            <Link to="/kontakt" onClick={() => setMenuOpen(false)} className="px-6 py-4 text-xl font-bold text-orange-500 border-b border-gray-100">Kontakt</Link>
            <Link to="/" onClick={() => setMenuOpen(false)} className="px-6 py-4 text-xl font-bold text-gray-800 hover:text-orange-500 border-b border-gray-100">← Zurück</Link>
          </nav>
        </div>
      )}

      {/* Hero */}
      <section className="py-20 bg-[#1e3a5f] text-white text-center">
        <h1 className="text-6xl md:text-8xl font-black text-orange-500 mb-6">Melde dich!</h1>
        <p className="text-lg max-w-2xl mx-auto px-4 text-white/80">
          Melden Sie sich für eine Beratung an – wir beantworten Ihre Fragen und stellen gemeinsam Ihren 
          persönlichen Trainingsplan zusammen. Nutzen Sie das Kontaktformular oder rufen Sie uns direkt an. 
          Wir freuen uns darauf, Sie kennenzulernen!
        </p>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <div className="bg-gray-50 rounded-xl p-8 mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Kontakt</h2>
                <div className="space-y-4">
                  <a href="tel:+41717555066" className="flex items-center gap-4 text-gray-700 hover:text-orange-500 transition-colors">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                      <Phone className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg font-medium">+41 71 755 50 66</span>
                  </a>
                  <a href="mailto:info@vitalis-sports.ch" className="flex items-center gap-4 text-gray-700 hover:text-orange-500 transition-colors">
                    <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-lg font-medium">info@vitalis-sports.ch</span>
                  </a>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Adresse</h2>
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-lg text-gray-700">Rorschacherstrasse 43</p>
                    <p className="text-lg text-gray-700">9450 Lüchingen/Altstätten</p>
                  </div>
                </div>
                
                {/* Google Maps Embed */}
                <div className="aspect-video rounded-xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2706.849138989992!2d9.548826315594!3d47.37594297917!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479b1c2f2b2b2b2b%3A0x2b2b2b2b2b2b2b2b!2sRorschacherstrasse%2043%2C%209450%20L%C3%BCchingen!5e0!3m2!1sde!2sch!4v1234567890"
                    className="w-full h-full"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Vitalis Sports Location"
                  />
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-50 rounded-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Oder melde dich über das Formular</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bereich</label>
                  <select
                    value={formData.bereich}
                    onChange={(e) => setFormData({...formData, bereich: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  >
                    <option value="">Bitte wählen...</option>
                    <option value="Fitness">Fitness</option>
                    <option value="Beckenboden">Beckenboden</option>
                    <option value="Tennis">Tennis</option>
                    <option value="Badminton">Badminton</option>
                    <option value="Squash">Squash</option>
                    <option value="Wellness">Wellness</option>
                    <option value="Anderes">Anderes</option>
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Vorname</label>
                    <input
                      type="text"
                      value={formData.vorname}
                      onChange={(e) => setFormData({...formData, vorname: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email-Adresse</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                  <input
                    type="tel"
                    value={formData.telefon}
                    onChange={(e) => setFormData({...formData, telefon: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nachricht</label>
                  <textarea
                    value={formData.nachricht}
                    onChange={(e) => setFormData({...formData, nachricht: e.target.value})}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-lg font-bold text-lg transition-colors disabled:opacity-50"
                >
                  {sending ? 'Wird gesendet...' : 'Senden'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={VITALIS_LOGO} alt="Vitalis Sports" className="h-10 mb-4 brightness-0 invert opacity-70" />
              <p className="text-sm">Dein familiäres Fitness- und Sportcenter im Rheintal.</p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Angebot</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/fitness" className="hover:text-white">Fitness</Link></li>
                <li><Link to="/racket" className="hover:text-white">Racket</Link></li>
                <li><Link to="/wellness" className="hover:text-white">Wellness</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Kontakt</h4>
              <ul className="space-y-2 text-sm">
                <li>Rorschacherstrasse 43</li>
                <li>9450 Lüchingen/Altstätten</li>
                <li>+41 71 755 50 66</li>
                <li>info@vitalis-sports.ch</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Extranet</h4>
              <p className="text-sm mb-4">Mitarbeiter-Bereich</p>
              <Link to="/login" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded text-sm font-medium">
                <Users className="h-4 w-4" />
                Anmelden
              </Link>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm">© 2026 Vitalis Sports</p>
            <div className="flex gap-6 text-sm">
              <a href="https://vitalis-sports.ch/impressum/" target="_blank" rel="noopener noreferrer" className="hover:text-white">Impressum</a>
              <a href="https://vitalis-sports.ch/datenschutzerklaerung/" target="_blank" rel="noopener noreferrer" className="hover:text-white">Datenschutz</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
